import React, { type ReactNode, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Modal, Spin } from 'antd';

import { MODAL_FOOTER_BUTTON_SIZE, MODAL_WIDTHS } from '@/shared/lib/constants';
import { Button } from '@/shared/ui/Button';
import { Icon } from '@/shared/ui/Icon';

import styles from './ConfirmModal.module.css';

type ConfirmModalProps = {
  open: boolean;
  message: ReactNode;
  icon?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  hideCancel?: boolean;
  maskClosable?: boolean;
  keyboard?: boolean;
  confirmButtonProps?: {
    danger?: boolean;
  };
  onConfirm: (() => void) | (() => Promise<void>);
  onCancel: () => void;
};

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  message,
  icon = <Icon name="fail" size={32} className={styles.icon} />,
  confirmText = '확인',
  cancelText = '취소',
  hideCancel = false,
  maskClosable = true,
  keyboard = true,
  confirmButtonProps,
  onConfirm,
  onCancel,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    const result = onConfirm();

    if (result instanceof Promise) {
      setIsLoading(true);
      try {
        await result;
        onCancel();
      } finally {
        setIsLoading(false);
      }
    } else {
      onCancel();
    }
  };

  const footer = (
    <>
      {!hideCancel && (
        <Button
          variant="outlined"
          color="standard"
          onClick={onCancel}
          disabled={isLoading}
          {...MODAL_FOOTER_BUTTON_SIZE}>
          {cancelText}
        </Button>
      )}
      <Button
        variant={confirmButtonProps?.danger ? 'critical' : 'filled'}
        color={confirmButtonProps?.danger ? 'critical' : 'standard'}
        onClick={handleConfirm}
        disabled={isLoading}
        {...MODAL_FOOTER_BUTTON_SIZE}>
        {isLoading ? '처리 중...' : confirmText}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={footer}
      closable={false}
      maskClosable={maskClosable}
      keyboard={keyboard}
      centered
      width={MODAL_WIDTHS.CONFIRM_ALERT}>
      <Spin spinning={isLoading} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}>
        <div className={styles.content}>
          {icon}
          <div className={styles.message}>{message}</div>
        </div>
      </Spin>
    </Modal>
  );
};
